import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Link,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link as RouterLink } from "react-router-dom";
import MarketPriceHistoryChart from "./MarketPriceHistoryChart";
import {
  HOME_MARKET_ITEMS,
  HOME_PRICE_HISTORY_URL,
  formatMarketPrice,
  getLatestPrice,
  getMarketHistoryEntry,
  marketsItemHref,
} from "../utils/marketPriceHistory";

function MarketHistorySkeleton() {
  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {HOME_MARKET_ITEMS.map((item) => (
        <Grid item xs={12} md={4} key={item.itemType}>
          <Skeleton
            variant="rectangular"
            animation="wave"
            height={236}
            sx={{ borderRadius: 1 }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export default function PlayerMarketsPreview() {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await fetch(HOME_PRICE_HISTORY_URL);
        if (!response.ok) {
          throw new Error(`Failed to load market history (${response.status})`);
        }
        const payload = await response.json();
        if (!cancelled) {
          setHistory(payload);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load market history");
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Player Markets Data
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Average prices over the last 12 months on the player market.
      </Typography>

      {loading ? (
        <MarketHistorySkeleton />
      ) : error ? (
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Market history is unavailable right now.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {HOME_MARKET_ITEMS.map((item) => {
            const entry = getMarketHistoryEntry(history, item.itemType);
            const series = entry?.series || [];
            const latest = getLatestPrice(series);
            return (
              <Grid item xs={12} md={4} key={item.itemType}>
                <Paper
                  variant="outlined"
                  component={RouterLink}
                  to={marketsItemHref(item.itemType)}
                  sx={{
                    display: "block",
                    height: "100%",
                    textDecoration: "none",
                    color: "inherit",
                    px: 1.5,
                    pt: 1.5,
                    pb: 2,
                    "&:hover": { boxShadow: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {entry?.label || item.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {latest == null
                          ? "No recent price"
                          : `Latest ${formatMarketPrice(latest)}`}
                      </Typography>
                    </Box>
                    {item.iconSrc ? (
                      <Box
                        component="img"
                        src={item.iconSrc}
                        alt=""
                        width={32}
                        height={32}
                        draggable={false}
                        sx={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          imageRendering: "pixelated",
                        }}
                      />
                    ) : null}
                  </Box>
                  <MarketPriceHistoryChart
                    series={series}
                    itemLabel={entry?.label || item.label}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 3 }}>
        <Link
          component={RouterLink}
          to="/markets"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontWeight: 600,
          }}
        >
          View All Markets Data
          <ArrowForwardIcon fontSize="small" />
        </Link>
      </Box>
    </Box>
  );
}
